import { Component, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { AuthenticationService } from '../../../services/authentication.service';
import { NgIcon } from '@ng-icons/core';
import { ButtonDirective } from '../../../components/button/button.directive';
import { DraftRoutesComponent } from '../draft-routes/draft-routes.component';
import { CustomerCreateModalComponent } from './customer-create-modal/customer-create-modal.component';
import type { CustomerType, CustomerCreationDraftRequest } from './customer-create-modal/customer-create-modal.interface';
import { SnackbarService } from '../../../services/snackbar.service';
import { CustomerService } from '../../../services/customer.service';
import { CustomerDropdownCacheService } from '../../../services/customer-dropdown-cache.service';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { canManageCustomerDrafts } from '../../../utils/customer-draft-roles';

@Component({
  selector: 'app-drafts-tab',
  imports: [TabLayoutComponent, NgIcon, ButtonDirective, DraftRoutesComponent, CustomerCreateModalComponent],
  providers: [{ provide: TabComponent, useExisting: DraftsTabComponent }],
  templateUrl: './drafts-tab.component.html',
  styleUrl: './drafts-tab.component.scss',
})
export class DraftsTabComponent extends TabComponent {
  override name = this.dictionaryService.get('Drafts');
  authService = inject(AuthenticationService);
  private http = inject(HttpClient);
  private snackbarService = inject(SnackbarService);
  private customerService = inject(CustomerService);
  private dropdownCache = inject(CustomerDropdownCacheService);
  private baseUrl = environment.apiBaseUrl;

  canManageDrafts = false;

  // Customer create modal state
  isCreateModalOpen = signal<boolean>(false);
  selectedCustomerType = signal<CustomerType>('doctor');

  public async main(): Promise<void> {
    const policy = this.authService.currentUserPolicy;
    this.canManageDrafts = canManageCustomerDrafts(policy?.role);
    // Dropdown data will be loaded lazily when modals are first opened
  }

  /**
   * Opens the customer create modal with the specified type
   */
  displayCustomerCreate = (type: CustomerType) => {
    if (!this.canManageDrafts) {
      return;
    }

    console.log('Opening create modal for:', type);
    this.selectedCustomerType.set(type);
    this.isCreateModalOpen.set(true);
  };

  /**
   * Handles modal close
   */
  handleCreateModalClose = () => {
    this.isCreateModalOpen.set(false);
  };

  /**
   * Handles customer creation with API-formatted payload
   */
  handleCreateCustomerApi = async (apiRequest: CustomerCreationDraftRequest) => {
    if (!this.canManageDrafts) {
      return;
    }

    console.log('Creating customer draft:', apiRequest);

    try {
      const endpoint = `${this.baseUrl}/customer-drafts/creation-drafts`;

      const response = await lastValueFrom(
        this.http.post<{ success: boolean; message: string; messageCode?: string }>(endpoint, apiRequest)
      );

      if (response.success) {
        this.snackbarService.showByCode(response.messageCode ?? 'S0801001');
        console.log('Customer draft created successfully:', response);
        // Trigger table refresh
        this.customerService.triggerDraftTableRefresh('Creation');
      } else {
        if (response.messageCode) {
          this.snackbarService.showByCode(response.messageCode);
        } else {
          this.snackbarService.showError(response.message || 'Failed to create customer draft');
        }
        console.error('Failed to create customer draft:', response.message);
      }
    } catch (error: any) {
      if (error?.error?.messageCode) {
        this.snackbarService.showByCode(error.error.messageCode);
      } else {
        this.snackbarService.showError(error?.error?.message || 'Failed to create customer draft');
      }
      console.error('Failed to create customer draft:', error);
    }
  };
}
